/**
 * The workspace route: the ONLY place modules meet (law 3). The shell
 * owns the frame and the projectId; ingest and viewer receive it as a
 * prop and never learn about each other.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, Input, Typography } from 'antd';
import { ProjectTabs } from './ProjectTabs';
import { subscribeStreamConnected } from '../api';
import { signOutUser, type User } from '../modules/auth';
import { IfcPreview, UploadPanel } from '../modules/ingest';
import { ViewerPanel, useElementsStatus } from '../modules/viewer';

function StreamStatus() {
  const [connected, setConnected] = useState(false);
  useEffect(() => subscribeStreamConnected(setConnected), []);
  // "Reconnecting" is the literal truth: the stream retries with
  // backoff forever, and a successful reconnect refetches everything.
  return (
    <div className="status-indicator" style={{ marginTop: '0.25rem' }}>
      <span className={`dot ${connected ? 'connected' : 'disconnected'}`}></span>
      {connected ? 'Live updates connected' : 'Reconnecting to live updates…'}
    </div>
  );
}

function ElementsStatus({ projectId }: { projectId: string }) {
  const { count, isError, isSettled } = useElementsStatus(projectId);
  // The stack STAYS UP (ruled 2026-08-25), so an unreachable server is
  // a fault rather than a sleeping VM. Wake-on-signin is retired: this
  // used to POST a wake endpoint on error and tell the reader to wait
  // two minutes, which is the wrong thing to say about a server that
  // should be answering.
  return (
    <div className="status-indicator">
      <span className={`dot ${isSettled ? 'connected' : 'disconnected'}`}></span>
      {isSettled && `${count} elements loaded`}
      {isError && 'Cannot reach the server.'}
      {!isSettled && !isError && 'Connecting to the server…'}
    </div>
  );
}

export function Workspace({ user, projectId }: { user: User; projectId: string }) {
  // The project id is the ROUTE's (APP_SPEC box 1): the input edits a
  // local buffer and navigates on every non-empty value (replace, so
  // keystrokes never stack in history). An emptied input stays local
  // until something is typed -- the URL always names a real project.
  const navigate = useNavigate();
  const [projectInput, setProjectInput] = useState(projectId);
  useEffect(() => setProjectInput(projectId), [projectId]);
  const onProjectInput = (value: string) => {
    setProjectInput(value);
    if (value)
      void navigate({
        to: '/projects/$projectId',
        params: { projectId: value },
        replace: true,
      });
  };
  // The instant preview owns the MAIN canvas while a picked file is
  // provisional: ingest elects the file (onPreview), the shell places
  // it over the viewer, and 'parsed' hands up null -- colored server
  // truth replaces the local gray without a click.
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  return (
    <div className="app-container" style={{ display: 'flex' }}>
      <aside className="sidebar glass-panel">
        <Link
          to="/projects"
          style={{ fontSize: '12px', color: '#9ca3af', textDecoration: 'none', display: 'inline-block', marginBottom: '0.3rem' }}
        >
          ← All projects
        </Link>
        <Typography.Title level={4} style={{ margin: '0.2rem 0 0.4rem' }}>
          Polywog
        </Typography.Title>
        <div style={{ margin: '0.4rem 0 0.6rem' }}>
          <ProjectTabs projectId={projectId} active="viewer" />
        </div>
        <div style={{ fontSize: '12px', color: '#9ca3af', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email ?? user.uid}</span>
          <Button size="small" onClick={() => void signOutUser()}>
            Sign out
          </Button>
        </div>
        <ElementsStatus projectId={projectId} />
        <StreamStatus />
        <div className="controls" style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Input
            value={projectInput}
            onChange={(e) => onProjectInput(e.target.value)}
            placeholder="project_id"
            aria-label="Project id"
          />
          <UploadPanel projectId={projectId} onPreview={setPreviewFile} />
        </div>
      </aside>

      <main style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <ViewerPanel projectId={projectId} />
        {previewFile && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 5, backgroundColor: '#111827' }}>
            <IfcPreview
              file={previewFile}
              onFailed={(reason) => {
                console.warn(`web-ifc preview declined (${reason}); websocket flow is the UX`);
                setPreviewFile(null);
              }}
            />
            <Button
              size="small"
              onClick={() => setPreviewFile(null)}
              style={{ position: 'absolute', top: '1rem', right: '1rem', zIndex: 6 }}
            >
              Close preview
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
